import { z } from 'zod';
import { isoDate } from '../schemas/common.js';
import { PUBLISH_BASIS_KINDS } from '../schemas/rules.js';

/**
 * Схемы входа инструментов MCP (specs/005-rules-mcp/contracts/mcp-tools.md, «Инструменты»). Корень — объект без
 * `anyOf` и `oneOf`, имена свойств верхнего уровня — латиница (research R3). Объединения — только внутри свойств.
 */

const versionId = z.number().int().positive();
const group = z.string().min(1).max(100).describe('Номер правила, как в rules_current');
const shortText = (max: number) => z.string().trim().min(1).max(max);

/** Режим ответа: краткий по умолчанию (research R10). */
const detail = z.enum(['short', 'full']).optional().describe('short — по умолчанию, full — с пояснениями');
/** Следующая страница: значение `page.next` прошлого ответа. */
const cursor = z.string().min(1).max(200).optional().describe('page.next прошлого ответа');
/** Необязательный ключ повтора изменяющего вызова, 24 часа (FR-053). */
const requestKey = z.string().min(8).max(100).optional().describe('Ключ повтора, 24 часа');
/** Номер долгого расчёта из ответа `{ pending: true, jobId }` (research R6). */
const jobId = z.string().min(1).max(100).optional().describe('jobId прошлого ответа «считается»');
/** Значения полей правила по ключу `кубик.поле`. */
const values = z.record(z.string().min(1).max(200), z.unknown());

const versionRef = z.strictObject({ versionId });
const target = z.union([z.enum(['current', 'future', 'draft']), versionRef]);
const goldensTarget = z.union([z.enum(['current', 'draft']), versionRef]);

/**
 * Версия скилла, как её пишет `skill:build`: `<движок>+<набор>.<папка>` — латиница, цифры, `.`, `+`, `-`, до 100
 * знаков. Другое — `VALIDATION`: сообщённая версия идёт в базу (`oauth_grant_usage.skill_version`) и журнал (T046).
 */
export const SKILL_VERSION_PATTERN = /^[0-9A-Za-z.+-]{1,100}$/;

export const RulesAboutInput = z.object({
  skillVersion: z
    .string()
    .regex(SKILL_VERSION_PATTERN)
    .optional()
    .describe('Версия скилла tabel-rules из SKILL.md'),
});

export const RulesCurrentInput = z.object({
  date: isoDate.optional(),
  section: z.string().min(1).max(100).optional(),
  detail,
  cursor,
});

export const RulesGroupInput = z.object({ group, date: isoDate.optional(), detail });

export const RulesHistoryInput = z.object({ from: isoDate.optional(), to: isoDate.optional(), cursor });

export const RulesDiffInput = z.object({
  from: versionId,
  to: z.union([versionId, z.literal('draft')]),
  cursor,
});

export const RulesCatalogInput = z.object({});

export const RulesRegistryInput = z.object({
  status: z.enum(['open', 'closed', 'all']).optional(),
  requestId: z.number().int().positive().optional(),
  cursor,
});

export const CheckRulesInput = z.object({
  target,
  date: isoDate.optional(),
  mode: z.enum(['findings', 'full']).optional(),
  detail,
  cursor,
  jobId,
});

export const CheckOptionsInput = z.object({ group, from: isoDate, to: isoDate, jobId });

export const CheckLawInput = z.object({ from: isoDate, to: isoDate });

export const CheckGoldensInput = z.object({ target: goldensTarget, cursor });

export const CheckTraceInput = z.object({ cursor });

export const UsageSummaryInput = z.object({
  group,
  from: isoDate,
  to: isoDate,
  byVersion: z.boolean().optional(),
});

/** Условие выборки дней (FR-023): любое сочетание; без условия — ссылка на день `link`. */
const SampleCondition = z.strictObject({
  group: group.optional(),
  flag: z.string().min(1).max(100).optional(),
  state: z.string().min(1).max(50).optional(),
  errorCode: z.string().min(1).max(100).optional(),
  versionId: versionId.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  hoursMin: z.number().min(0).max(48).optional(),
  hoursMax: z.number().min(0).max(48).optional(),
  changedByDraft: z.boolean().optional(),
});

export const DaysSampleInput = z.object({
  condition: SampleCondition.optional(),
  link: z.string().min(1).max(500).optional().describe('Ссылка на день из интерфейса табеля'),
  limit: z.number().int().min(1).max(20).optional(),
});

export const PlanDraftGetInput = z.object({});

export const PlanDraftSetInput = z.object({
  etag: z.string().min(1).max(200).nullable().optional().describe('Ревизия черновика; нет черновика — null'),
  group,
  values,
  addFromCatalog: z.boolean().optional(),
  notApply: z.boolean().optional(),
  requestKey,
});

export const PlanDraftRevertInput = z.object({ etag: z.string().min(1).max(200), group, requestKey });

export const PlanImpactInput = z.object({
  group: group.optional(),
  values: values.optional(),
  scope: z.enum(['change', 'draft']),
  validFrom: isoDate.optional(),
  jobId,
});

/** Придуманный день для проверки (FR-033). */
export const SyntheticDay = z.strictObject({
  date: isoDate,
  /** Вид дня — как `DAY_TYPES` движка. */
  dayType: z.enum(['рабочий', 'выходной', 'праздник']),
  preholiday: z.boolean().optional(),
  passes: z
    .array(
      z.strictObject({
        at: z.string().regex(/^\d{2}:\d{2}$/, 'Время чч:мм'),
        dir: z.enum(['in', 'out']),
        nextDay: z.boolean().optional(),
      }),
    )
    .max(20),
  schedule: z.string().min(1).max(100).optional(),
});

export const PlanCheckDayInput = z.object({
  day: z.union([
    z.strictObject({ golden: z.string().min(1).max(100) }),
    z.strictObject({ synthetic: SyntheticDay }),
    z.strictObject({ ref: z.string().min(1).max(200) }),
  ]),
  scope: z.enum(['change', 'draft']),
  group: group.optional(),
  values: values.optional(),
});

export const PlanFreezeInput = z.object({
  validFrom: isoDate,
  basis: z.strictObject({
    kind: z
      .enum(PUBLISH_BASIS_KINDS)
      .describe(
        'Почему меняем: решение руководства, приказ или распоряжение, исправление ошибки, изменение закона. Номер приказа добавляет человек на странице проекта',
      ),
    decided: shortText(500).describe('Что решили, без ФИО'),
    questionCode: z
      .string()
      .regex(/^Q-\d{2,3}$/)
      .optional(),
  }),
  requestKey,
});

export const PlanGetInput = z.object({ planId: z.number().int().positive() });

export const PlanRuleRequestInput = z.object({
  text: shortText(2000).describe('Что нужно, без ФИО и номеров'),
  refs: z.array(z.string().min(1).max(200)).max(20),
  questionCode: z
    .string()
    .regex(/^Q-\d{2,3}$/)
    .optional(),
  needsQuestion: z.boolean(),
  requestKey,
});
